<template>
    <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20">
        <!-- Header with buttons -->
        <div class="rlms-flex rlms-items-center rlms-justify-between rlms-flex-wrap rlms-gap-10">
            <div class="rlms-fs-lg rlms-fw-600">My Groups</div>
            <div class="rlms-flex rlms-items-center rlms-gap-5">
                <button class="rlms-btn rlms-btn-primary" @click="showModal = true">
                    Add New Group
                </button>
                <svg v-if="selectedGroupIds.length > 0" class="rlms-popup-btn del-btn" @click="deleteSelected"
                    width="50" height="46" viewBox="0 0 50 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="50" height="46" rx="12" fill="#FFE6DD" />
                    <path
                        d="M36.25 15.4756C32.0875 15.0631 27.9 14.8506 23.725 14.8506C21.25 14.8506 18.775 14.9756 16.3 15.2256L13.75 15.4756"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M20.625 14.2125L20.9 12.575C21.1 11.3875 21.25 10.5 23.3625 10.5H26.6375C28.75 10.5 28.9125 11.4375 29.1 12.5875L29.375 14.2125"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path
                        d="M33.5625 19.4258L32.75 32.0133C32.6125 33.9758 32.5 35.5008 29.0125 35.5008H20.9875C17.5 35.5008 17.3875 33.9758 17.25 32.0133L16.4375 19.4258"
                        stroke="#FF5664" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M22.9141 28.625H27.0766" stroke="#FF5664" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <path d="M21.875 23.625H28.125" stroke="#FF5664" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                </svg>
            </div>
        </div>

        <!-- Groups Table or Empty State -->
        <div class="rlms-bg-white rlms-br-20">
            <div v-if="groups.length === 0" class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20 rlms-items-center">
                <div class="rlms-text-center rlms-fw-700 rlms-fs-lg">Creating Learner Groups</div>
                <div class="rlms-flex rlms-flex-col rlms-gap-desk30-mob-20 rlms-justify-center rlms-items-center"
                    style="min-height: 600px;">
                    <img :src="groupKidsReadingBook" width="300" />
                    <div class="rlms-fw-600 rlms-fs-md">No Group</div>
                    <p style="max-width: 604px;">
                        No Learner Groups have been defined for this organization. Please tap the 'Add a Group' button
                        above to create a Learner Group.
                    </p>
                    <button class="rlms-btn rlms-btn-primary" @click="showModal = true">
                        Add New Group
                    </button>
                </div>
            </div>

            <!-- Group list could go here if groups.length > 0 -->
            <div v-else>
                <div class="rlms-table">
                    <div class="rlms-table-inner">
                        <table>
                            <thead>
                                <tr>
                                    <th>Groups Name</th>
                                    <th>Authorized Users</th>
                                    <th>Learners Count</th>
                                    <th>Created</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="group in groups" :key="group.id">
                                    <td>
                                        <label class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-fc rlms-us-none">
                                            <input type="checkbox" class="rlms-checkbox" :value="group.id"
                                                v-model="selectedGroupIds" />
                                            <div class="rlms-flex rlms-items-center rlms-gap-10 rlms-w-mc">
                                                <!-- Group avatars -->
                                                <div class="rlms-avatar-group" style="padding-left:23px;">
                                                    <div v-for="i in 3" :key="i" class="rlms-avatar">
                                                        <img :src="noImage" />
                                                    </div>
                                                </div>
                                                <p class="rlms-fw-500">{{ group.name }}</p>
                                            </div>
                                        </label>
                                    </td>
                                    <td>{{ group.users_count ?? 0 }}</td>
                                    <td>{{ group.learners_count ?? 0 }}</td>
                                    <td>
                                        <p class="rlms-text-extra-3">{{ formatDate(group.created_at) }}</p>
                                    </td>
                                    <td>
                                        <RouterLink :to="`/organizations/${organizationId}/group/${group.id}`"
                                            class="rlms-flex rlms-items-center rlms-gap-5 rlms-cursor-pointer">
                                            <img :src="eyeIcon" />
                                            <p class="rlms-text-primary-500">View</p>
                                        </RouterLink>
                                    </td>
                                </tr>

                                <tr v-if="groups.length === 0">
                                    <td colspan="5" class="text-center">No groups found.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Group Modal -->
        <AddEditGroupModal v-if="showModal" @close="showModal = false;" @saved="init()"
            :organizationId="organizationId" />
    </div>
</template>

<script setup>
import AddEditGroupModal from '@/components/AddEditGroupModal.vue';
import api from '@/services/axios';
import { formatDate } from '@/utils/format-helpers';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import confirmationIcon from '@/assets/images/confirmation.png';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import { showConfirmDialog, showDialog, showToast } from '@/utils/swal';
import groupKidsReadingBook from '@/assets/images/group-kids-reading-book.png'
import noImage from '@/assets/images/no-image.png'
import eyeIcon from '@/assets/images/eye.svg'

const route = useRoute();
const loader = useLoaderStore();

const organizationId = Number(route.params.organization ?? 0);
const selectedGroupIds = ref([]);
const groups = ref([]);
const showModal = ref(false);

onMounted(() => {
    init();
});

const init = () => {
    showModal.value = false;
    fetchData();
}

const fetchData = async () => {
    try {
        loader.show();
        const { data } = await api.get(`/organizations/${organizationId}/groups`);
        groups.value = data.data.groups;
    } catch (error) {
        handleError(error)
    } finally {
        loader.hide();
    }
}

const deleteSelected = async () => {
    if (!selectedGroupIds.value.length) {
        showDialog({
            title: 'No Selection',
            html: 'Please select at least one group to delete.',
            icon: 'warning'
        })
        return;
    }

    const result = await showConfirmDialog({
        title: 'Are you sure?',
        html: '<p id="text-message">Are you sure you want to delete the selected groups?</p><span class="rlms-text-danger-500" id="delete-confirm"> This action will permanently remove and cannot be undone. </span>',
        confirmButtonText: 'Delete',
        imageUrl: confirmationIcon
    });

    if (result.isConfirmed) {
        try {
            loader.show();

            await api.delete(`/organizations/${organizationId}/groups/1`, {
                data: {
                    selected_group: selectedGroupIds.value
                }
            });

            showToast({
                icon: 'success',
                title: 'Deleted! ',
                text: 'The selected groups have been deleted successfully!',
            });

            init();
        } catch (error) {
            handleError(error)
        } finally {
            loader.hide();
        }
    }
};

</script>
